import {
  recognizedCampaignUnitHeadings,
  recognizedImposedSalesPriceHeadings,
  recognizedPurchasePriceHeadings,
  recognizedSalesPriceHeadings,
  recognizedSecondaryDiscountPercentage,
  recognizedTertiaryDiscountPercentage,
} from '../importUtils';
import {
  ImportColumnConfig,
  ImportItemsConfig,
  spreadsheetBoolean,
  spreadsheetNumber,
  spreadsheetString,
} from '@retail/products/components';
import {
  BaseCampaignImportItem,
  BrandCampaignImportItem,
  MemberCampaignImportItem,
} from './CampaignImportItem';
import {
  recognizedGtinHeadings,
  recognizedMgItemNrHeadings,
  recognizedSupplierIdHeadings,
} from '@retail/utils';
import * as yup from 'yup';

const baseColumns: ImportColumnConfig<Extract<keyof BaseCampaignImportItem, string>>[] = [
  { name: 'mgItemNumber', recognizedColumnNames: recognizedMgItemNrHeadings },
  { name: 'gtinCode', recognizedColumnNames: recognizedGtinHeadings },
  { name: 'mgSupplierId', recognizedColumnNames: recognizedSupplierIdHeadings },
  { name: 'campaignUnit', recognizedColumnNames: recognizedCampaignUnitHeadings },
  { name: 'salesPrice', recognizedColumnNames: recognizedSalesPriceHeadings },
  {
    name: 'secondaryDiscountPercentage',
    recognizedColumnNames: recognizedSecondaryDiscountPercentage,
  },
  {
    name: 'tertiaryDiscountPercentage',
    recognizedColumnNames: recognizedTertiaryDiscountPercentage,
  },
  { name: 'purchasePrice', recognizedColumnNames: recognizedPurchasePriceHeadings },
];

const baseSchemaFields = {
  mgItemNumber: spreadsheetNumber().required(),
  gtinCode: spreadsheetString(),
  mgSupplierId: spreadsheetNumber(),
  campaignUnit: spreadsheetString(),
  salesPrice: spreadsheetNumber(),
  secondaryDiscountPercentage: spreadsheetNumber(),
  tertiaryDiscountPercentage: spreadsheetNumber(),
  purchasePrice: spreadsheetNumber(),
};

export abstract class CampaignItemsImportConfig<
  T extends BaseCampaignImportItem
> extends ImportItemsConfig<T> {}

export class BrandCampaignItemsImportConfig extends CampaignItemsImportConfig<BrandCampaignImportItem> {
  private constructor() {
    super();
  }

  static instance = new BrandCampaignItemsImportConfig();

  columns: ImportColumnConfig<Extract<keyof BrandCampaignImportItem, string>>[] = [
    ...baseColumns,
    { name: 'imposedSalesPrice', recognizedColumnNames: recognizedImposedSalesPriceHeadings },
  ];

  schema = yup.object({
    ...baseSchemaFields,
    imposedSalesPrice: spreadsheetBoolean(),
  });

  templateExampleData = {
    mgItemNumber: [123123123, null],
    gtinCode: [null, '45645645645621'],
    mgSupplierId: [70000, null],
    campaignUnit: ['STK', 'KRT'],
    salesPrice: [50, 5000],
    secondaryDiscountPercentage: [20, null],
    tertiaryDiscountPercentage: [null, null],
    purchasePrice: [20, 2000],
    imposedSalesPrice: [true, null],
  };
}

export class MemberCampaignItemsImportConfig extends CampaignItemsImportConfig<MemberCampaignImportItem> {
  private constructor() {
    super();
  }

  static instance = new MemberCampaignItemsImportConfig();

  columns = baseColumns;

  schema = yup.object(baseSchemaFields);

  templateExampleData = {
    mgItemNumber: [123123123, null],
    gtinCode: [null, '45645645645621'],
    mgSupplierId: [70000, null],
    campaignUnit: ['STK', 'KRT'],
    salesPrice: [50, 5000],
    secondaryDiscountPercentage: [20, null],
    tertiaryDiscountPercentage: [null, null],
    purchasePrice: [20, 2000],
    imposedSalesPrice: [true, null],
  };
}
